// Minimal fetch wrapper — cookies + CSRF header otomatik.
// M3+ daha zengin client (error mapping, retry, vb.) buraya eklenir.

interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  /** İptal/zaman aşımı (form otomatik kaydı 30 sn — UX P1). Yalnız apiRaw iletir. */
  signal?: AbortSignal;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Kickout sözleşmesi (Müşteri Katılımcı Rolü, Task 8) — client modunda herhangi bir
 * whitelist endpoint'i top-level `error === "PARTICIPATION_ENDED"` dönerse (403),
 * çağıran kod (api/apiRaw/uploadFile) fark etmeksizin merkezi olarak burada yakalanır.
 * `CLIENT_FORBIDDEN` KASITLI OLARAK tetiklemez — bu beklenen "kapalı endpoint" durumu
 * (whitelist dışı istek), sessiz/normal hata akışına bırakılır.
 * Handler App/ClientShell tarafında register edilir (navigate + kullanıcı mesajı).
 */
let kickoutHandler: (() => void) | null = null;
export function registerKickoutHandler(fn: (() => void) | null): void {
  kickoutHandler = fn;
}
function maybeKickout(body: { error?: string } | null | undefined): void {
  if (body?.error === "PARTICIPATION_ENDED") kickoutHandler?.();
}

/**
 * CSRF token — auth-store login/bootstrap response'undan localStorage'a yazılır.
 * @fastify/csrf-protection cookie'de SECRET tutar, header'da TOKEN bekler — bunlar
 * farklı string'ler (token = HMAC of secret). Cookie'yi okumak yanlış olur.
 */
const CSRF_STORAGE_KEY = "mahir.csrfToken";
function readCsrfToken(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.localStorage.getItem(CSRF_STORAGE_KEY) ?? undefined;
}
/**
 * KPI-28 — ham gövdeli (JSON olmayan) özel isteklerde CSRF başlığı (`api()`/`uploadFile` dışı
 * fetch'ler için; token yoksa boş nesne). `readCsrfToken` dışa açılmaz, yalnız başlık şekli.
 */
export function csrfHeaders(): Record<string, string> {
  const csrf = readCsrfToken();
  return csrf ? { "x-csrf-token": csrf } : {};
}
export function setCsrfToken(token: string | null): void {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem(CSRF_STORAGE_KEY, token);
  else window.localStorage.removeItem(CSRF_STORAGE_KEY);
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  // Content-Type sadece body varsa set edilir. Fastify body'siz istekte
  // Content-Type görürse "Body cannot be empty" hatası verir.
  const headers: Record<string, string> = { ...opts.headers };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const csrf = readCsrfToken();
  if (csrf && opts.method && opts.method !== "GET") {
    headers["x-csrf-token"] = csrf;
  }

  const res = await fetch(`/api/v1${path}`, {
    method: opts.method ?? "GET",
    credentials: "include",
    headers,
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  });

  if (!res.ok) {
    let errBody: { error?: string; message?: string; details?: unknown } = {};
    try {
      errBody = (await res.json()) as typeof errBody;
    } catch {
      // non-json response
    }
    maybeKickout(errBody);
    throw new ApiError(
      res.status,
      errBody.error ?? "unknown",
      errBody.message ?? `HTTP ${res.status}`,
      errBody.details,
    );
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

/**
 * Raw fetch — 4xx/5xx'i exception'a çevirmez, { ok, status, body } döner.
 * STALE_WRITE (409) + VALIDATION_FAILED (422) gibi body'si anlamlı hata
 * response'ları için. M4 form-fill batch upsert kullanır.
 */
export async function apiRaw<T>(
  path: string,
  opts: ApiOptions = {},
): Promise<{ ok: boolean; status: number; body: T }> {
  const headers: Record<string, string> = { ...opts.headers };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const csrf = readCsrfToken();
  if (csrf && opts.method && opts.method !== "GET") {
    headers["x-csrf-token"] = csrf;
  }
  const res = await fetch(`/api/v1${path}`, {
    method: opts.method ?? "GET",
    credentials: "include",
    headers,
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // non-json
  }
  if (!res.ok) maybeKickout(body as { error?: string } | null);
  return { ok: res.ok, status: res.status, body: body as T };
}

/**
 * Binary file upload — PUT request with credentials + CSRF token + raw body.
 * MinIO backend proxy route'una (`/api/v1/files/upload?...`) PUT eder.
 *
 * Native fetch CSRF token header'ı otomatik set etmez; bu helper localStorage'dan
 * okuyup x-csrf-token header'ına manuel ekler (api() ile aynı pattern).
 */
export async function uploadFile(uploadUrl: string, file: File | Blob): Promise<void> {
  const headers: Record<string, string> = {
    "Content-Type": (file instanceof File ? file.type : "") || "application/octet-stream",
  };
  const csrf = readCsrfToken();
  if (csrf) headers["x-csrf-token"] = csrf;

  const res = await fetch(uploadUrl, {
    method: "PUT",
    credentials: "include",
    headers,
    body: file,
  });
  if (!res.ok) {
    let errMsg = `Upload failed: HTTP ${res.status.toString()}`;
    try {
      const errBody = (await res.json()) as { error?: string; message?: string };
      if (errBody.message) errMsg = errBody.message;
      maybeKickout(errBody);
    } catch {
      // non-json body
    }
    throw new Error(errMsg);
  }
}

/**
 * Binary file upload + ilerleme yüzdesi — Geçmiş Rapor Aktarımı (Task 12, Regresyon
 * analizi G2/RG5). `uploadFile`'ın XHR ikizi: native `fetch` upload ilerlemesini
 * raporlayamadığı için (`fetch` request body progress event'i yok) burada XHR
 * kullanılır. Aynı CSRF header + `credentials`/`withCredentials` + `maybeKickout`
 * sözleşmesi korunur; hata `ApiError` olarak reddedilir (uploadFile plain Error atar —
 * tüketicileri farklı, `report-import-ui.ts` `ApiError` bekler).
 * `uploadFile` ve `AttachmentUploader` bu değişiklikten ETKİLENMEZ (TODO-UX-07).
 */
export function uploadFileWithProgress(
  uploadUrl: string,
  file: File | Blob,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader(
      "Content-Type",
      (file instanceof File ? file.type : "") || "application/octet-stream",
    );
    const csrf = readCsrfToken();
    if (csrf) xhr.setRequestHeader("x-csrf-token", csrf);

    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable && e.total > 0) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      let errBody: { error?: string; message?: string } = {};
      try {
        errBody = JSON.parse(xhr.responseText) as typeof errBody;
      } catch {
        // non-json response
      }
      maybeKickout(errBody);
      reject(
        new ApiError(
          xhr.status,
          errBody.error ?? "unknown",
          errBody.message ?? `HTTP ${xhr.status.toString()}`,
        ),
      );
    };

    xhr.onerror = () => {
      reject(new ApiError(0, "network_error", "Yükleme başarısız — ağ hatası"));
    };

    xhr.send(file);
  });
}
